/*
  Meta Pixel — consent gate and PageView owner.

  This component is the whole of the Pixel's presence on the page. Nothing is
  in public/index.html any more: the base code that used to sit there loaded
  fbevents.js and sent PageView on every document load, before anyone had been
  asked, which is exactly what the opt-in requirement forbids. So the script is
  injected from metaPixelLoader.js, on the grant, and PageView is fired from
  here.

  Being the only PageView owner is also what keeps the count right. There is no
  longer a document-load event to skip: this component tracks the page it is
  looking at, whichever way the visitor arrived at it — a document load, a
  client-side navigation, or answering the banner while standing on it.

  Three guards keep the count honest:
    - lastTrackedPath is module scope, so StrictMode's double effect in
      development and any remount of this component both count one PageView;
    - it is a path, not a counter, so a grant while already on a tracked page
      cannot add a second event for that page;
    - every send goes through tracking.js, which refuses without consent — so
      the effects below can run as often as React likes before the visitor has
      answered and still produce nothing.

  Revocation: the pixel cannot be unloaded once it is on the page, so a revoke
  latches tracking off for the rest of this page's life (see
  metaPixelLoader.js) and no further event is sent. Re-accepting takes effect
  from the next document load.
*/
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

import { subscribeToConsent } from "./consent";
import { disableMetaPixel } from "./metaPixelLoader";
import { trackPageView } from "./tracking";

// Survives remounts and StrictMode's double effect invocation.
let lastTrackedPath = null;

/* A PageView for `path`, unless that path has already been counted. Returns
   nothing useful; whether it actually left the browser is decided downstream
   by the consent gate in tracking.js. */
function trackPath(path) {
  if (path === lastTrackedPath) return;
  /* Recorded only when a send was really made. Without consent trackPageView
     reports false, and the path stays uncounted so that answering the banner
     on this page still produces its PageView. */
  if (trackPageView()) lastTrackedPath = path;
}

export default function MetaPixel() {
  const location = useLocation();
  const path = `${location.pathname}${location.search}`;

  /* The consent subscription is mounted once and must not be torn down and
     rebuilt on every navigation, so it reads the current path from a ref
     rather than closing over it. */
  const pathRef = useRef(path);
  pathRef.current = path;

  // Every page the visitor reaches, including the one they landed on.
  useEffect(() => {
    trackPath(path);
  }, [path]);

  // And the moment they answer the banner, for the page they are on then.
  useEffect(
    () =>
      subscribeToConsent((state) => {
        if (state.marketing) trackPath(pathRef.current);
        else disableMetaPixel();
      }),
    []
  );

  return null;
}

/** Test-only. lastTrackedPath is module state on purpose. */
export function resetPageViewsForTests() {
  lastTrackedPath = null;
}
